import { useCallback, useEffect, useRef, useState } from 'react';
import { createPageVoice, quietClip, type PageVoice } from '../utils/pageVoice';

/**
 * THE CHAT'S ANSWERS, READ ALOUD BY THE DEVICE'S OWN VOICE (VOICE1, size A - the 130th, 2026-10-11).
 *
 * His words: "can we add a voice to the AI chat ... if it could talk back to me ... kind of like a person." His answer
 * to the three sizes put to him: "we can do on the phone as you recommend" - the phone's own voice, the same chat, no
 * service and no cost.
 *
 * ⭐⭐ THE RULE THIS IS BUILT ON WAS MEASURED, NOT ASSUMED (`docs/planning/2026-10-10-voice1-probe/`: the iPhone simulator
 *    on iOS 26.0.1, as a Safari tab AND installed to the home screen - the same in both):
 *      - speech asked for when nothing has been spoken inside a tap yet: NOTHING. No start, no end, no error.
 *      - speech asked for INSIDE a tap: it starts and ends.
 *      - after that, speech asked for seconds after a tap: it starts and ends.
 *    So the FIRST SOUND MUST COME FROM HIS FINGER. `turnOn` speaks its confirmation inside the tap that turns it on, and
 *    `prime` speaks one silent word inside the tap that sends a message - for the page that was opened with it already on.
 * ⛔ A DEVICE THAT WILL NOT SPEAK DOES NOT SAY SO. Speech that has not STARTED a moment after it was asked for is
 *    reported as `silent`, so the screen can tell him to tap the speaker instead of leaving him listening to nothing.
 * ⭐ ONE UTTERANCE PER SENTENCE (`speechText.ts`): a stop lands between two sentences, never inside a figure.
 * ⛔ Nothing is spoken that is not written on the screen - this is handed the transcript's own words, and only those.
 * ⭐ THE 132ND (slice 3, the back-and-forth mode): `turnOn(true)` turns it on with the SILENT word instead of the
 *    confirmation - that tap also opens the microphone, and the app must not talk into it (the probe measured every
 *    one of its speech events at volume 0, so the silent word is the measured unlock). `busy()` answers, at the instant
 *    it is asked, whether anything is still owed to his ear - `speaking` is a render behind.
 * ⭐ THE 134TH (VOICE-PICK1) - A VOICE HE PICKS. His phone, on step two: "how do i get it to sund less robotic?" Until
 *    now this set the language and picked no voice. MEASURED FIRST (`docs/planning/2026-10-11-voice-pick-probe/`, the
 *    iPhone simulator on iOS 26.0.1 - a Safari tab and the home-screen app are offered the same list):
 *      - 68 voices are there the instant the page loads and no `voiceschanged` ever comes; a desktop browser has NONE
 *        at load and all of them 19 ms later, with that event. So the list is read at once AND on the event.
 *      - 25 are English: 6 ordinary ones (US, UK, Australia, Ireland, India, South Africa - each the phone's smallest
 *        grade of voice) and 19 of Apple's old novelty voices, told apart by their id (`voiceURI`).
 *      - EVERY voice says it is the default - so "the phone's own" cannot be found in the list; it is NO voice set.
 *      - choosing one changes what is said: the same sentence took 8.5 s in one voice and 28.5 s in another.
 *      - a stop on a sentence BEING SAID frees the voice at once (the next began 10 ms after the tap).
 *        ⛔ But a sentence queued and stopped in the SAME instant is still waited out in silence (8.7 s, twice) -
 *        so nothing here ever asks for speech and then stops it in one call: the stop always comes FIRST.
 *      - a native menu is NOT a tap: speech asked for in its change, with nothing said inside a tap yet, never began.
 *        So choosing a voice says nothing, and hearing it is a button of its own (`sample`).
 *    ⛔ WITH NOTHING CHOSEN, NOTHING HAS CHANGED: en-US and no voice, exactly as it was.
 *    ⚠️ NOT measured: his phone's own list, a voice he downloaded in Settings showing up for the app, how any of
 *       them SOUNDS (every sentence in the probe was said at volume 0).
 * ⭐ THE 135TH (VOICE1-B, slice 0) - THE SERVER'S VOICE BEHIND THE SAME CONTROLS. His phone, on the picker: "the voices
 *    are bad ... The only US voice ... sounds like it's the same iPhone voice ... it would be great to be able to fix
 *    that." A better voice cannot come from the phone; it comes as a clip from our server, played by the page
 *    (`utils/pageVoice.ts` - its rule was measured too). Handed in as `server`:
 *      - the server HAS a voice: the picker gains ONE entry, first. Picked, every sentence said ALOUD goes to the
 *        page's own player; the silent unlock word stays the phone's.
 *      - ⛔ the server has none, or it is not picked: NOTHING HAS CHANGED - not one line of the phone's path runs
 *        differently, and no clip is ever asked for or played.
 *      - ⛔ every tap that may be followed by sound (turning it on, Send, "Hear it") plays one quiet clip AND says
 *        the phone's silent word inside the call: the page may then play clips by itself, and the phone's own voice
 *        is free to step in for a clip that does not come.
 *      - ⛔ waiting for a clip is not "the phone will not speak": the watchdog below is for the phone's own voice.
 */

/** Remembered per device: 'on' or 'off'. A phone he reads aloud on is not his desk. */
export const READ_ALOUD_KEY = 'apex.assistant.readAloud';

/** How long the device has to BEGIN speaking before it is called silent. A started sentence may then take as long as it likes. */
const MUST_START_WITHIN_MS = 1500;
/** Said, audibly, inside the tap that turns it on: the sound check and the unlock in one. */
const TURNED_ON = 'Read aloud is on.';
/** Said at volume 0 inside a Send tap. A real word: an empty utterance is not known to count as speech. */
const PRIMING_WORD = 'ready';

/** Remembered per device: the id (`voiceURI`) of the voice he picked. Absent = the phone's own, no voice set. */
export const VOICE_KEY = 'apex.assistant.voice';
/** Said aloud in the picked voice when he asks to hear it: a figure, because figures are what a voice is judged on. */
export const VOICE_SAMPLE = 'This is the voice that will read your answers. The ask is $3,550,000.';
/** Apple's old novelty voices (Bells, Zarvox, Bad News ...) carry this id - measured: 19 of an iPhone's 25 English voices. */
const NOVELTY_ID = 'com.apple.speech.synthesis.voice.';
/** Where a voice is from, as he would say it. A place not listed is shown as the device gave it. */
const PLACE: Record<string, string> = {
  'en-US': 'US', 'en-GB': 'UK', 'en-AU': 'Australia', 'en-IE': 'Ireland', 'en-IN': 'India', 'en-ZA': 'South Africa',
};

/** The picker's id for the server's voice - never a device's own id (those are `voiceURI`s). */
export const SERVER_VOICE = 'apex.server-voice';
/** The server's voice, when it has one: whose it is, and one sentence as a clip (null when none comes). */
export interface ServerVoice { supplier: string; fetchClip: (text: string, signal: AbortSignal) => Promise<Blob | null> }
/** Whose voice it is, as the menu says it. A supplier not listed is still offered, plainly. */
const SUPPLIER_LABEL: Record<string, string> = {
  elevenlabs: 'ElevenLabs voice',
  deepgram: 'Deepgram voice',
  'stand-in': 'Stand-in tone (a test, not a voice)',
};

/** One voice he can pick. `novelty` ones are listed after the ordinary ones, under their own heading. */
export interface VoiceOption { id: string; label: string; novelty: boolean }

export interface ReadAloud {
  /** This browser has a voice at all. False hides the control. */
  supported: boolean;
  /** He has turned reading on (here, or last time on this device). */
  on: boolean;
  /** Something is being said or is waiting to be. */
  speaking: boolean;
  /** Speech was asked for and never began - the device is refusing, and has not said so. */
  silent: boolean;
  /** ⛔ CALL INSIDE THE TAP. Turns it on and speaks in the same call: its confirmation, or - quietly - one silent word. */
  turnOn: (quietly?: boolean) => void;
  turnOff: () => void;
  /** ⛔ CALL INSIDE A TAP (Send). One silent word, so the answer that arrives seconds later may be spoken. */
  prime: () => void;
  /** Says these sentences after whatever is already being said. Nothing while it is off. */
  say: (sentences: string[]) => void;
  /** Cuts off what is being said and forgets what was waiting. It stays on. */
  stop: () => void;
  /** Right now, is anything being said or waiting to be? */
  busy: () => boolean;
  /** The English voices this device offers a page: the ordinary ones, then the novelty ones. Empty = nothing to pick. */
  voices: VoiceOption[];
  /** The id of the voice he picked, when the device offers it. '' = the phone's own. */
  voice: string;
  /** Picks a voice (or '' for the phone's own) and remembers it on this device. ⛔ Says nothing - a menu is not a tap. */
  chooseVoice: (id: string) => void;
  /** ⛔ CALL INSIDE THE TAP. Cuts off what is being said, then says a sample aloud in the picked voice. */
  sample: () => void;
}

const deviceVoice = (): SpeechSynthesis | null => {
  if (typeof window === 'undefined' || typeof SpeechSynthesisUtterance === 'undefined') return null;
  return ('speechSynthesis' in window && window.speechSynthesis) || null;
};

/** Every voice the device lists for a page - none where the browser has no list to give. */
const deviceVoices = (): SpeechSynthesisVoice[] => deviceVoice()?.getVoices?.() ?? [];

const offered = (): VoiceOption[] => {
  const english = deviceVoices()
    .filter((v) => /^en[-_]/i.test(v.lang))
    .map((v) => ({
      id: v.voiceURI,
      // A name that already carries its language in brackets (a desktop's "Eddy (English (United Kingdom))") says enough.
      label: v.name.includes('(') ? v.name : `${v.name} - ${PLACE[v.lang] ?? v.lang}`,
      novelty: v.voiceURI.startsWith(NOVELTY_ID),
    }));
  return [...english.filter((v) => !v.novelty), ...english.filter((v) => v.novelty)];
};

const rememberedVoice = (): string => {
  try { return localStorage.getItem(VOICE_KEY) ?? ''; } catch { return ''; }
};
const rememberVoice = (id: string): void => {
  try {
    if (id) localStorage.setItem(VOICE_KEY, id);
    else localStorage.removeItem(VOICE_KEY);
  } catch { /* a browser that keeps nothing still speaks in the voice he picked, until the page closes */ }
};

const remembered = (): boolean => {
  try { return localStorage.getItem(READ_ALOUD_KEY) === 'on'; } catch { return false; }
};
const remember = (on: boolean): void => {
  try { localStorage.setItem(READ_ALOUD_KEY, on ? 'on' : 'off'); } catch { /* a browser that keeps nothing still reads aloud */ }
};

export const useReadAloud = (options: { onIdle?: () => void; server?: ServerVoice | null } = {}): ReadAloud => {
  const supported = deviceVoice() !== null;
  const [on, setOn] = useState<boolean>(() => supported && remembered());
  const [speaking, setSpeaking] = useState(false);
  const [silent, setSilent] = useState(false);
  const [voices, setVoices] = useState<VoiceOption[]>(offered);
  const [picked, setPicked] = useState<string>(rememberedVoice);
  /** The picked id as it is THIS instant - a sentence can be asked for inside the tap that picked it. */
  const pickedNow = useRef(picked);

  /** `on` as it is THIS instant - `turnOn` and the `say` that follows it can run inside one tap, before a render. */
  const onRef = useRef(on);
  /** Bumped by every stop: an utterance cut off reports back later, and must not be counted against what came after. */
  const generation = useRef(0);
  const waiting = useRef(0);
  const begun = useRef(false);
  /** An ANSWER was among what was just said - only then is "finished" worth telling the caller. */
  const saidAnAnswer = useRef(false);
  const watchdog = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onIdle = useRef(options.onIdle);
  onIdle.current = options.onIdle;
  /** VOICE1-B: the server's voice as it is THIS render, and the page's own player - made the first time it is needed. */
  const server = useRef<ServerVoice | null>(options.server ?? null);
  server.current = options.server ?? null;
  const page = useRef<PageVoice | null>(null);
  /** The server's voice is the one to use right now: it is picked AND the server has one. */
  const serverNow = (): ServerVoice | null => (pickedNow.current === SERVER_VOICE ? server.current : null);

  const disarm = (): void => {
    if (watchdog.current !== null) clearTimeout(watchdog.current);
    watchdog.current = null;
  };
  const arm = (): void => {
    if (watchdog.current !== null || begun.current) return;
    watchdog.current = setTimeout(() => { watchdog.current = null; setSilent(true); }, MUST_START_WITHIN_MS);
  };

  /** The phone's own voice says one thing: every sentence while the server's voice is not in use, and the one whose clip did not come while it is. */
  const utter = (text: string, volume: number, onstart: () => void, onend: () => void): void => {
    const utterance = new SpeechSynthesisUtterance(text);
    // VOICE-PICK1: the voice he picked, looked up in the device's list as it is NOW (the list can arrive late, and a
    // voice can stop being offered) - and that voice's own English. Nothing picked, or not offered: as it always was.
    const chosen = pickedNow.current ? deviceVoices().find((v) => v.voiceURI === pickedNow.current) : undefined;
    if (chosen) utterance.voice = chosen;
    utterance.lang = chosen?.lang ?? 'en-US';
    utterance.volume = volume;
    utterance.onstart = onstart;
    utterance.onend = onend;
    utterance.onerror = onend;
    deviceVoice()?.speak(utterance);
  };

  const pageVoice = (): PageVoice => (page.current ??= createPageVoice({
    fetchClip: (text, signal) => server.current?.fetchClip(text, signal) ?? Promise.resolve(null),
    // The phone's own voice steps in - and a phone that will not say it either is noticed, as it always was.
    viaDevice: (text, on) => { arm(); utter(text, 1, on.start, on.end); },
    audio: () => new Audio(),
    toUrl: (clip) => URL.createObjectURL(clip),
    freeUrl: (url) => URL.revokeObjectURL(url),
    quietClip: URL.createObjectURL(quietClip()),
  }));

  const ask = (text: string, volume: number): void => {
    if (!deviceVoice()) return;
    const mine = generation.current;
    const started = (): void => {
      if (mine !== generation.current) return;
      begun.current = true;
      disarm();
      setSilent(false);
    };
    const finished = (): void => {
      if (mine !== generation.current) return;
      begun.current = false;
      waiting.current = Math.max(0, waiting.current - 1);
      // VOICE1-B: between two CLIPS the next may still be on its way - the server being slow, not the phone refusing.
      if (waiting.current > 0) { if (!page.current?.busy()) arm(); return; }
      disarm();
      setSpeaking(false);
      if (saidAnAnswer.current) {
        saidAnAnswer.current = false;
        onIdle.current?.();
      }
    };
    waiting.current += 1;
    setSpeaking(true);
    // VOICE1-B: a sentence said ALOUD goes to the page's own player when the server's voice is the one picked.
    if (volume > 0 && serverNow()) { pageVoice().say(text, { start: started, end: finished }); return; }
    arm();
    utter(text, volume, started, finished);
  };

  /** ⛔ INSIDE A TAP. With the server's voice in use: the page may play clips later (measured). True when it is in use. */
  const unlockForClips = (): boolean => {
    if (!serverNow()) return false;
    pageVoice().unlock();
    return true;
  };

  const stop = useCallback((): void => {
    generation.current += 1;
    waiting.current = 0;
    begun.current = false;
    saidAnAnswer.current = false;
    disarm();
    page.current?.stop();
    deviceVoice()?.cancel();
    setSpeaking(false);
  }, []);

  const turnOn = useCallback((quietly = false): void => {
    if (!deviceVoice()) return;
    stop();
    onRef.current = true;
    setOn(true);
    remember(true);
    // ⛔ Synchronously, in this call: the tap that turned it on is what lets the device speak at all.
    // VOICE1-B: with the server's voice the confirmation is a clip - so the phone's own silent word is said here too.
    if (unlockForClips() || quietly) ask(PRIMING_WORD, 0);
    if (!quietly) ask(TURNED_ON, 1);
  }, [stop]);

  const turnOff = useCallback((): void => {
    stop();
    onRef.current = false;
    setOn(false);
    setSilent(false);
    if (deviceVoice()) remember(false);
  }, [stop]);

  const prime = useCallback((): void => {
    if (!onRef.current || waiting.current > 0) return;
    unlockForClips();
    ask(PRIMING_WORD, 0);
  }, []);

  const say = useCallback((sentences: string[]): void => {
    if (!onRef.current) return;
    for (const sentence of sentences) {
      if (!sentence.trim()) continue;
      saidAnAnswer.current = true;
      ask(sentence, 1);
    }
  }, []);

  const busy = useCallback((): boolean => waiting.current > 0, []);

  const chooseVoice = useCallback((id: string): void => {
    pickedNow.current = id;
    setPicked(id);
    rememberVoice(id);
  }, []);

  const sample = useCallback((): void => {
    // ⛔ The stop FIRST, then the sentence (measured: one queued and stopped in the same instant is waited out).
    stop();
    if (unlockForClips()) ask(PRIMING_WORD, 0);
    ask(VOICE_SAMPLE, 1);
  }, [stop]);

  // The device's list: read when the screen opens (an iPhone has it at once) and whenever the device says it changed
  // (a desktop browser hands it over a moment after the page loads).
  useEffect(() => {
    const device = deviceVoice();
    const refresh = (): void => setVoices(offered());
    device?.addEventListener?.('voiceschanged', refresh);
    return () => device?.removeEventListener?.('voiceschanged', refresh);
  }, []);

  // Leaving the screen stops the voice: a sentence about one deal must not finish over another page.
  useEffect(() => () => {
    generation.current += 1;
    disarm();
    page.current?.stop();
    deviceVoice()?.cancel();
  }, []);

  // What the picker shows as picked: his choice when the device offers it, otherwise the phone's own.
  // VOICE1-B: the server's voice, when it has one, is ONE more entry - first, because it is the better one.
  const offeredVoices = options.server
    ? [{ id: SERVER_VOICE, label: SUPPLIER_LABEL[options.server.supplier] ?? 'The server\'s voice', novelty: false }, ...voices]
    : voices;
  const voice = offeredVoices.some((v) => v.id === picked) ? picked : '';

  return { supported, on, speaking, silent, turnOn, turnOff, prime, say, stop, busy, voices: offeredVoices, voice, chooseVoice, sample };
};
